/**
 * WordPress Beacon Dashboard — agency-wide health across every WP
 * site running the Iceberg Beacon plugin. Powered by
 * `GET /api/wp/beacon-overview`.
 *
 *  • Fresh / Stale / Offline traffic-light per site
 *  • Outdated plugin & theme counts, WP + PHP version drift
 *  • Pending-command counters
 *  • "Update all outdated plugins everywhere" one-click action
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Radar, Loader2, RefreshCw, AlertTriangle, Zap, CheckCircle2,
  Signal, SignalHigh, SignalMedium, SignalLow, Package, PanelsTopLeft,
} from "lucide-react";

const errMsg = (e, fb) => e?.response?.data?.detail || fb;

function freshnessBadge(row) {
  const cfg = {
    fresh:     { color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: <SignalHigh className="h-3 w-3" />, label: "fresh" },
    stale:     { color: "bg-amber-50 text-amber-800 border-amber-200",       icon: <SignalMedium className="h-3 w-3" />, label: "stale" },
    offline:   { color: "bg-red-50 text-red-700 border-red-200",             icon: <SignalLow className="h-3 w-3" />, label: "offline" },
    no_beacon: { color: "bg-zinc-100 text-zinc-500 border-zinc-200",         icon: <Signal className="h-3 w-3" />, label: "no beacon" },
  }[row.beacon_freshness] || { color: "bg-zinc-100 text-zinc-500 border-zinc-200", icon: <Signal className="h-3 w-3" />, label: "unknown" };
  return (
    <Badge variant="outline" className={`rounded-sm text-[10px] inline-flex items-center gap-1 ${cfg.color}`}>
      {cfg.icon} {cfg.label}
    </Badge>
  );
}

function fmtMinutesAgo(m) {
  if (m == null) return "never";
  if (m < 60) return `${m} min ago`;
  if (m < 24 * 60) return `${Math.round(m / 60)} h ago`;
  return `${Math.round(m / 60 / 24)} d ago`;
}

export default function WpBeaconDashboardPage() {
  const [data, setData] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const [ov, res] = await Promise.all([
        api.get("/wp/beacon-overview"),
        api.get("/wp/beacon-command-results?limit=100"),
      ]);
      setData(ov.data);
      setResults(res.data.commands || []);
    } catch (e) { toast.error(errMsg(e, "Failed to load beacon dashboard")); }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s so the dashboard reflects incoming beacons.
  useEffect(() => {
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [load]);

  const autoUpdateAll = async () => {
    const total = data?.totals?.outdated_plugins_and_themes || 0;
    if (!total) { toast.info("Nothing to update — every site is current."); return; }
    if (!window.confirm(
      `Queue update commands for ${total} outdated plugin(s) across `
      + `${data.totals.beacon_configured} site(s)?\n\n`
      + `Each site's beacon will pick these up on its next poll `
      + `(5-60 min depending on interval).`)) return;
    setRunning(true);
    try {
      const { data: r } = await api.post("/wp/beacon-auto-update-all");
      toast.success(
        `Queued ${r.queued_count} update${r.queued_count === 1 ? "" : "s"} `
        + `across ${r.sites_touched} site${r.sites_touched === 1 ? "" : "s"}.`);
      await load();
    } catch (e) { toast.error(errMsg(e, "Auto-update-all failed")); }
    finally { setRunning(false); }
  };

  const totals = data?.totals || {};
  const rows = data?.sites || [];

  const kpis = useMemo(() => ([
    { label: "Sites tracked",     v: totals.sites, color: "text-zinc-900" },
    { label: "Fresh (< 2× interval)", v: totals.fresh, color: "text-emerald-700" },
    { label: "Stale (< 24h)",     v: totals.stale, color: "text-amber-700" },
    { label: "Offline (> 24h)",   v: totals.offline, color: "text-red-700" },
    { label: "Outdated items",    v: totals.outdated_plugins_and_themes, color: "text-orange-700" },
    { label: "Pending commands",  v: totals.pending_commands, color: "text-[#002FA7]" },
  ]), [totals]);

  return (
    <div className="p-6 space-y-5 max-w-[1400px]" data-testid="wp-beacon-dashboard">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-overline flex items-center gap-2">
            <Radar className="h-3 w-3" /> WordPress · Beacon
          </div>
          <h1 className="font-display font-extrabold text-3xl mt-1">Beacon Dashboard</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Health of every WordPress site running the Iceberg Beacon
            plugin. Each site polls us on WP-Cron — a green row means
            we&apos;ve heard from it within 2× its polling interval.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => load()} disabled={refreshing}
            data-testid="beacon-dashboard-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button size="sm"
            className="bg-[#002FA7] hover:bg-[#001f70] text-white"
            onClick={autoUpdateAll}
            disabled={running || !(totals.outdated_plugins_and_themes > 0)}
            data-testid="beacon-auto-update-all">
            {running ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Zap className="h-3.5 w-3.5 mr-1" />}
            Update all outdated ({totals.outdated_plugins_and_themes || 0})
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {kpis.map((k) => (
          <Card key={k.label} className="rounded-sm">
            <CardContent className="p-3">
              <div className="label-overline">{k.label}</div>
              <div className={`font-display font-extrabold text-2xl mt-0.5 ${k.color}`}>
                {(k.v ?? 0).toLocaleString()}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {loading && !data ? (
        <Card className="rounded-sm"><CardContent className="p-8 flex items-center gap-2 text-zinc-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading beacon dashboard…
        </CardContent></Card>
      ) : (
        <Card className="rounded-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]" data-testid="beacon-sites-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-zinc-100 bg-zinc-50/50">
                    <th className="text-left p-2">Site</th>
                    <th className="text-left p-2">State</th>
                    <th className="text-left p-2">Last seen</th>
                    <th className="text-left p-2">Poll</th>
                    <th className="text-right p-2">Plugins</th>
                    <th className="text-right p-2">Themes</th>
                    <th className="text-left p-2">Core</th>
                    <th className="text-left p-2">WP / PHP</th>
                    <th className="text-right p-2">Pending</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={9} className="py-10 text-center text-sm text-zinc-400 italic">
                      No WP sites yet — add one on the WordPress page.
                    </td></tr>
                  ) : rows.map((r) => (
                    <tr key={r.site_id}
                      className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50/50"
                      data-testid={`beacon-row-${r.site_id}`}>
                      <td className="p-2">
                        <Link to={`/wordpress/sites/${r.site_id}`}
                          className="font-semibold text-[#002FA7] hover:underline"
                          data-testid={`beacon-open-site-${r.site_id}`}>
                          {r.name}
                        </Link>
                        <div className="text-[10px] text-zinc-400 font-mono truncate max-w-[280px]">
                          {r.base_url}
                        </div>
                      </td>
                      <td className="p-2">
                        {r.beacon_configured
                          ? freshnessBadge(r)
                          : <Badge variant="outline"
                              className="rounded-sm text-[10px] bg-zinc-100 text-zinc-500 border-zinc-200">
                              not installed
                            </Badge>}
                      </td>
                      <td className="p-2 text-zinc-600 font-mono text-[11px]">
                        {fmtMinutesAgo(r.beacon_minutes_ago)}
                      </td>
                      <td className="p-2 text-zinc-500 font-mono text-[11px]">
                        {r.beacon_interval_minutes}m
                      </td>
                      <td className="p-2 text-right">
                        {r.plugin_updates_count > 0 ? (
                          <Badge variant="outline"
                            className="rounded-sm text-[10px] bg-orange-50 text-orange-700 border-orange-200">
                            {r.plugin_updates_count}
                          </Badge>
                        ) : <span className="text-zinc-300">—</span>}
                      </td>
                      <td className="p-2 text-right">
                        {r.theme_updates_count > 0 ? (
                          <Badge variant="outline"
                            className="rounded-sm text-[10px] bg-orange-50 text-orange-700 border-orange-200">
                            {r.theme_updates_count}
                          </Badge>
                        ) : <span className="text-zinc-300">—</span>}
                      </td>
                      <td className="p-2">
                        {r.core_update_available ? (
                          <Badge variant="outline"
                            className="rounded-sm text-[10px] bg-red-50 text-red-700 border-red-200 inline-flex items-center gap-0.5">
                            <AlertTriangle className="h-2.5 w-2.5" /> {r.core_update_available}
                          </Badge>
                        ) : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
                      </td>
                      <td className="p-2 text-zinc-600 font-mono text-[10px]">
                        {r.wp_version || "—"} · {r.php_version || "—"}
                      </td>
                      <td className="p-2 text-right">
                        {r.pending_commands > 0 ? (
                          <Badge variant="outline"
                            className="rounded-sm text-[10px] bg-[#002FA7] text-white border-[#002FA7] inline-flex items-center gap-0.5">
                            <Package className="h-2.5 w-2.5" /> {r.pending_commands}
                          </Badge>
                        ) : <span className="text-zinc-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <p className="text-[10px] text-zinc-400 flex items-center gap-1">
        <PanelsTopLeft className="h-3 w-3" /> Auto-refreshes every 30s ·
        Beacons post outbound only, so this dashboard is not affected by
        client-side firewalls (Imunify360 / Wordfence / Cloudflare).
      </p>

      {/* Command results feed — every beacon-executed command across
          every site, newest first. Success/failure at a glance. */}
      <Card className="rounded-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Package className="h-4 w-4 text-zinc-700" />
            <h3 className="font-display font-bold text-base">Recent command results</h3>
            <Badge variant="outline" className="rounded-sm text-[10px]">
              {results.length}
            </Badge>
          </div>
          {results.length === 0 ? (
            <div className="text-sm text-zinc-400 italic py-3 text-center">
              No commands have been queued yet. Use <b>Update all outdated</b>
              {" "}above or open a site&apos;s beacon panel to queue one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]" data-testid="beacon-results-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-zinc-100">
                    <th className="text-left p-1.5">Site</th>
                    <th className="text-left p-1.5">Command</th>
                    <th className="text-left p-1.5">Target</th>
                    <th className="text-left p-1.5">Status</th>
                    <th className="text-left p-1.5">Result</th>
                    <th className="text-left p-1.5">Queued</th>
                    <th className="text-left p-1.5">Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.command_id}
                      className="border-b border-zinc-100 last:border-b-0"
                      data-testid={`beacon-result-row-${r.command_id}`}>
                      <td className="p-1.5">
                        <div className="font-semibold">{r.site_name || r.site_id}</div>
                        <div className="text-[10px] font-mono text-zinc-400 truncate max-w-[240px]">
                          {r.site_url}
                        </div>
                      </td>
                      <td className="p-1.5 font-mono text-[11px]">{r.type}</td>
                      <td className="p-1.5 font-mono text-[10px] text-zinc-500">
                        {r.params?.plugin || r.params?.stylesheet || r.params?.name || "—"}
                        {r.params?.target_version && ` → ${r.params.target_version}`}
                      </td>
                      <td className="p-1.5">
                        <Badge variant="outline" className={`rounded-sm text-[10px] ${
                          r.status === "complete" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : r.status === "failed" ? "bg-red-50 text-red-700 border-red-200"
                              : r.status === "dispatched" ? "bg-sky-50 text-sky-700 border-sky-200"
                                : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                          {r.status}
                        </Badge>
                      </td>
                      <td className="p-1.5 text-[10px] text-zinc-600 max-w-[320px] truncate">
                        {r.result?.error
                          ? <span className="text-red-700">{r.result.error}</span>
                          : Array.isArray(r.result?.messages)
                            ? r.result.messages.join(" · ")
                            : (typeof r.result?.messages === "string" ? r.result.messages : "—")}
                      </td>
                      <td className="p-1.5 text-[10px] font-mono text-zinc-500">
                        {r.created_at ? String(r.created_at).slice(0, 16).replace("T", " ") : "—"}
                      </td>
                      <td className="p-1.5 text-[10px] font-mono text-zinc-500">
                        {r.completed_at ? String(r.completed_at).slice(0, 16).replace("T", " ") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
